import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Printer, Download, MoreHorizontal } from "lucide-react";
import DOMPurify from 'dompurify';
import { FormattedAIResponse } from "@/components/FormattedAIResponse";
import { SearchResults } from "@/components/search/SearchResults";
import { LARAResultsRenderer } from "@/components/agents/LARAResultsRenderer";
import { WikipediaCard } from "@/components/agents/WikipediaCard";
import { ColorPaletteResponse } from "@/components/agents/ColorPaletteResponse";
import { PPTMakerViewer } from "@/components/agents/PPTMakerViewer";
import { FlowChartRenderer } from "@/components/agents/FlowChartRenderer";
import WeatherCard from "@/components/WeatherCard";
import { ParallelSearchResponse } from "@/components/ParallelSearchResponse";
import { GoogleAIRichResults } from "@/components/search/GoogleAIRichResults";

const DocView: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as any;
  const message = state?.message as any;
  const isDarkMode = state?.isDarkMode ?? false;
  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement | null>(null);

  if (!message) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center space-y-4">
          <div className="text-2xl font-semibold">Nothing to show</div>
          <div className="text-sm text-gray-500">No message data was provided.</div>
          <div className="flex items-center justify-center gap-2">
            <Button variant="outline" onClick={() => navigate(-1)}>Go Back</Button>
          </div>
        </div>
      </div>
    );
  }

  const looksLikeHtml = typeof message.content === "string" && /<\w+[^>]*>/.test(message.content);

  const parsedGoogleAi = React.useMemo(() => {
    if (typeof message?.content !== 'string') return null;
    const raw = message.content.trim();
    if (!raw.startsWith('{')) return null;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.__googleai) return parsed;
    } catch { }
    return null;
  }, [message?.content]);

  let processedHtml = typeof message.content === "string" ? message.content : "";
  if (looksLikeHtml && !message.isHTML) {
    const isSearchResult = message?.selectedAgent?.name === "Google Search" ||
      processedHtml.includes('class="search-result"') ||
      processedHtml.includes('Google Search Results');
    if (!isSearchResult) {
      processedHtml = processedHtml.replace(
        /<a\s+(?:[^>]*?\s+)?href=(["'])(.*?)\1/g,
        (_match, quote, url) => `<a href=${quote}${url}${quote} style="text-decoration: underline; color: ${isDarkMode ? "white" : "#2563eb"}"`
      );
    }
  }

  const contentRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [message]);

  React.useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  const handlePrint = React.useCallback(() => {
    try { window.print(); } catch { }
  }, []);

  const handleDownloadAsImage = React.useCallback(async () => {
    // Ensure this is visible to finally{} even if try{} fails early
    let disabledFontLinks: HTMLLinkElement[] = [];
    try {
      const domtoimage = await ensureDomToImage();
      const node = document.getElementById('doc-content');
      if (!node) return;

      // Temporarily disable cross-origin Google Fonts stylesheets to avoid SecurityError on cssRules
      try {
        document.querySelectorAll('link[rel="stylesheet"]').forEach((lnk) => {
          const linkEl = lnk as HTMLLinkElement;
          const href = linkEl.href || '';
          if (href.startsWith('https://fonts.googleapis.com')) {
            // Preserve original rel so we can restore later
            linkEl.setAttribute('data-orig-rel', linkEl.rel);
            linkEl.rel = 'preload';
            linkEl.as = 'style';
            disabledFontLinks.push(linkEl);
          }
        });
      } catch { }

      // Ensure all images are loaded to prevent blank areas
      const imgs = Array.from(node.querySelectorAll('img')) as HTMLImageElement[];
      await Promise.all(
        imgs.map(img =>
          img.complete
            ? Promise.resolve()
            : new Promise<void>(resolve => {
              const done = () => resolve();
              img.addEventListener('load', done, { once: true });
              img.addEventListener('error', done, { once: true });
            })
        )
      );

      // Use full scroll size to avoid clipped capture
      const width = Math.ceil(node.scrollWidth);
      const height = Math.ceil(node.scrollHeight);

      const scale = Math.max(1, Math.min(3, window.devicePixelRatio || 1));

      // Capture with safe styles and without cross-origin font stylesheets
      const blob = await domtoimage.toBlob(node, {
        width: Math.max(1, Math.floor(width * scale)),
        height: Math.max(1, Math.floor(height * scale)),
        bgcolor: isDarkMode ? '#202020' : '#ffffff',
        cacheBust: true,
        style: {
          overflow: 'visible',
          background: isDarkMode ? '#202020' : '#ffffff',
          transformOrigin: 'top left',
          width: `${width}px`,
          height: `${height}px`,
          transform: scale === 1 ? 'none' : `scale(${scale})`,
        },
        filter: (n: Node) => {
          // Skip elements explicitly marked no-print
          if (n instanceof HTMLElement && n.classList.contains('no-print')) return false;
          return true;
        },
        onClone: (clonedDoc: Document) => {
          try {
            // Remove Google Fonts stylesheets to avoid SecurityError on cssRules
            clonedDoc.querySelectorAll('link[rel="stylesheet"]').forEach((lnk) => {
              const href = (lnk as HTMLLinkElement).href || '';
              if (href.startsWith('https://fonts.googleapis.com')) {
                lnk.parentElement?.removeChild(lnk);
              }
            });
            // Set a safe fallback font on the clone
            clonedDoc.body.style.fontFamily = 'Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial';
            // Force visible overflow in the clone
            const el = clonedDoc.getElementById('doc-content');
            if (el) {
              el.style.overflow = 'visible';
              el.style.maxHeight = 'none';
              el.style.background = '#ffffff';
            }

            // Inject CSS to strip borders/outlines/box-shadows/rings that may appear during rasterization
            const styleEl = clonedDoc.createElement('style');
            styleEl.setAttribute('data-domtoimage-reset', 'true');
            styleEl.textContent = `
              html, body { background: #ffffff !important; }
              #doc-content { background: #ffffff !important; }
              /* Make inner backgrounds transparent to avoid gray blocks from utilities */
              .prose, .prose * { background-color: transparent !important; }
              [class*="bg-"], [style*="background"], [style*="background-color"] { background: transparent !important; background-color: transparent !important; }
              *, *::before, *::after { outline: none !important; box-shadow: none !important; }
              [class*="border"], [class*="ring"], [class*="outline"], [style*="border"], [style*="outline"], [style*="box-shadow"] {
                border: none !important; border-color: transparent !important; outline: none !important; box-shadow: none !important;
              }
              a, button, input, textarea, select, pre, code { outline: none !important; box-shadow: none !important; border: none !important; }
              .prose :where(figure, table, pre, code) { border: none !important; box-shadow: none !important; background: transparent !important; }
              pre, code, kbd, samp { background: transparent !important; }
            `;
            clonedDoc.head.appendChild(styleEl);
          } catch { }
        },
      });

      if (!blob) throw new Error('Failed to render image');
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = 'oreog-response.png';
      link.href = url;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      console.error('Download image failed:', e);
    } finally {
      // Restore any disabled Google Fonts links
      try {
        disabledFontLinks.forEach((lnk) => {
          const orig = lnk.getAttribute('data-orig-rel') || 'stylesheet';
          lnk.rel = orig;
          lnk.removeAttribute('data-orig-rel');
          lnk.as = '' as any;
        });
      } catch { }
    }
  }, [isDarkMode]);

  const ensureDomToImage = async (): Promise<any> => {
    if ((window as any).domtoimage) return (window as any).domtoimage;
    await new Promise<void>((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/dom-to-image-more@3.3.0/dist/dom-to-image-more.min.js';
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('Failed to load dom-to-image-more'));
      document.body.appendChild(s);
    });
    return (window as any).domtoimage;
  };

  return (
    <div className={`${isDarkMode ? "bg-[#202020] text-white" : "bg-white text-black"} min-h-screen`}>
      {/* Print styles to force white bg, black text, and hide controls */}
      <style>{`
        @media print {
          body { background: #ffffff !important; color: #000000 !important; }
          .dark, .dark * { color: #000000 !important; }
          .no-print { display: none !important; }
          .prose, .prose * { color: #000000 !important; }
          img { filter: none !important; }
        }
      `}</style>

      <div className="max-w-3xl mx-auto px-4 py-5">
        <div className="flex items-center justify-between border-b border-gray-200/50 pb-3 mb-4 no-print">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")} className="rounded-2xl">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="font-semibold flex items-center gap-2">
              <img src="https://res.cloudinary.com/dx0aqgtyg/image/upload/v1759925893/image_wja3qz.png" alt="OREOG" className="h-8" />
              <span>Oreog</span>
            </div>
          </div>
          <div className="relative no-print" ref={menuRef}>
            <Button variant="outline" size="sm" onClick={() => setMenuOpen((v) => !v)} className="rounded-2xl inline-flex items-center gap-1">
              <MoreHorizontal className="w-4 h-4" />
            </Button>
            {menuOpen && (
              <div className={`absolute right-0 mt-2 w-40 rounded-2xl border overflow-hidden ${isDarkMode ? 'bg-[#2a2a2a] border-[#3a3a3a] text-white' : 'bg-white border-gray-200 text-black'} shadow-lg z-10`}>
                <button onClick={() => { setMenuOpen(false); handlePrint(); }} className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-[#3a3a3a] flex items-center gap-2">
                  <Printer className="w-4 h-4" /> Print
                </button>
                <button onClick={() => { setMenuOpen(false); handleDownloadAsImage(); }} className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-[#3a3a3a] flex items-center gap-2">
                  <Download className="w-4 h-4" /> Download
                </button>
              </div>
            )}
          </div>
        </div>

        <div ref={contentRef} id="doc-content" className="prose prose-sm max-w-none dark:prose-invert">
          {/* Render LARA multi-agent results */}
          {message.isLARA && message.laraResults ? (
            <LARAResultsRenderer results={message.laraResults} isDarkMode={isDarkMode} />
          ) : /* Render PPT Maker presentation */
            message.agentData?.ppt ? (
              <PPTMakerViewer ppt={message.agentData.ppt} isDarkMode={isDarkMode} />
            ) : /* Render Flowchart */
              message.agentData?.flowchart ? (
                <FlowChartRenderer spec={message.agentData.flowchart} isDarkMode={isDarkMode} />
              ) : /* Render Weather Card */
                message.weatherData ? (
                  (() => {
                    const wd: any = message.weatherData;
                    const transformed = wd?.current ? {
                      location: wd.location || 'Unknown Location',
                      temperature: Math.round(wd.current.temperature || wd.current.temp || 0),
                      description: wd.current.description || wd.current.weather?.[0]?.description || 'No description',
                      humidity: wd.current.humidity || 0,
                      windSpeed: Math.round(wd.current.windSpeed || wd.current.wind_speed || 0),
                      pressure: wd.current.pressure || 0,
                      feelsLike: Math.round(wd.current.feelsLike || wd.current.feels_like || 0),
                      visibility: wd.current.visibility || null,
                      icon: wd.current.icon || wd.current.weather?.[0]?.icon || '01d',
                      backgroundImage: wd.current.backgroundImage || 'https://images.unsplash.com/photo-1504608524841-42fe6f032b4b?w=400&h=300&fit=crop',
                      isDay: wd.current.isDay !== undefined ? wd.current.isDay : true,
                      condition: wd.current.condition || wd.current.weather?.[0]?.main || 'Clear',
                      uvIndex: wd.uvIndex || wd.current.uvIndex,
                      airQuality: wd.airQuality?.description,
                      tempMax: wd.current.tempMax || wd.current.temp_max || wd.daily?.[0]?.temp_max || wd.daily?.[0]?.tempMax || (wd.current.temperature ? wd.current.temperature + 3 : null),
                      tempMin: wd.current.tempMin || wd.current.temp_min || wd.daily?.[0]?.temp_min || wd.daily?.[0]?.tempMin || (wd.current.temperature ? wd.current.temperature - 5 : null),
                      rain: wd.current.rain || wd.current.rain?.['1h']
                    } : null;
                    return transformed ? <WeatherCard data={transformed} /> : <div className="text-xs">{typeof wd === 'string' ? wd : JSON.stringify(wd, null, 2)}</div>;
                  })()
                ) : /* Render Wikipedia Card */
                  message.agentData?.wikipedia ? (
                    <WikipediaCard data={message.agentData.wikipedia} isDarkMode={isDarkMode} />
                  ) : /* Render Web Search Results */
                    message.searchResults ? (
                      <div className="space-y-3">
                        {typeof message.content === 'string' && message.content.trim() && (
                          <FormattedAIResponse
                            content={message.content}
                            isStreaming={false}
                            isDarkMode={isDarkMode}
                          />
                        )}
                        <SearchResults results={message.searchResults} isDarkMode={isDarkMode} renderMode="full" hideSourcesButton={true} />
                      </div>
                    ) : /* Render Deep Research */
                      message.model === 'deep-research' ? (
                        <ParallelSearchResponse
                          content={typeof message.content === 'string' ? message.content.replace(/<think>[\s\S]*?<\/think>/i, '') : ''}
                          isDarkMode={isDarkMode}
                          sources={(message as any).parallelSources || []}
                        />
                      ) : /* Render Google AI rich results */
                        parsedGoogleAi ? (
                          <div className="space-y-3">
                            <FormattedAIResponse
                              content={(() => {
                                const fromBlocks = (parsedGoogleAi?.data?.text_blocks && Array.isArray(parsedGoogleAi.data.text_blocks))
                                  ? parsedGoogleAi.data.text_blocks
                                    .map((b: any) => (typeof b?.snippet === 'string' ? b.snippet : (typeof b?.text === 'string' ? b.text : '')))
                                    .filter(Boolean)
                                    .join('\n\n')
                                  : '';
                                return String(fromBlocks || parsedGoogleAi?.text || '');
                              })()}
                              isStreaming={false}
                              isDarkMode={isDarkMode}
                            />
                            <GoogleAIRichResults data={parsedGoogleAi?.data || {}} isDarkMode={isDarkMode} />
                          </div>
                        ) : /* Render Color Palette */
                          message.selectedAgent === 'color-palette' ? (
                            (() => {
                              try {
                                const paletteData = typeof message.content === 'string' ? JSON.parse(message.content) : message.content;
                                return <ColorPaletteResponse palette={paletteData} isDarkMode={isDarkMode} />;
                              } catch (e) {
                                return <FormattedAIResponse content={typeof message.content === "string" ? message.content : JSON.stringify(message.content)} isStreaming={false} isDarkMode={isDarkMode} />;
                              }
                            })()
                          ) : /* Render Image */
                            message.isImage ? (
                              <div className="flex justify-center">
                                <img
                                  src={message.content}
                                  alt="Generated content"
                                  className="max-w-full h-auto rounded-lg"
                                />
                              </div>
                            ) : /* Render HTML content */
                              looksLikeHtml && typeof message.content === "string" && !message.content.includes("```") ? (
                                <div
                                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(processedHtml) }}
                                  className={`prose prose-sm max-w-none ${isDarkMode ? "prose-invert" : ""}`}
                                  style={{ color: isDarkMode ? "#ffffff" : "#000000", ...(message.isHTML ? { all: "revert" } : {}) }}
                                />
                              ) : /* Default: Render as formatted markdown */
                                (
                                  <FormattedAIResponse
                                    content={typeof message.content === "string" ? message.content : JSON.stringify(message.content)}
                                    isStreaming={false}
                                    isDarkMode={isDarkMode}
                                  />
                                )}
        </div>
      </div>
    </div>
  );
};

export default DocView;
